import { useState, useEffect, useRef } from 'react'
import classnames from 'classnames/bind'
import { useMutation } from 'react-query'
import { BigNumber } from 'bignumber.js'
import { throttle, random } from 'lodash-es'

// Libs
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { setLikes } from '@/api/setLikes'
import { LIVE_TRACK_EVENT, trackLiveEvent } from '@/methods/liveTracking'
import { setLike, setLikeList } from '@/redux/reducers/socket'
import { handleGtmLiveInteraction } from '@/methods/handleGtmEvent'

// Components
import Icon from '@/assets/icons'
import Ballon from '../../sharedComponents/Ballon'
import HorizontalEmoji from '../../sharedComponents/HorizontalEmoji'

// Constants
import { WS_EVENT, WS_EVENT_SHOP_STATE } from '@/constants/socketEvent'
import { GtmLiveInteractionType } from '@/constants/gtmEvent'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface LikeProps {
  className: string
  likes: number
  isHost?: boolean
}

const throttleDuration = 1000

const Like = (props: LikeProps) => {
  const { className, likes, isHost = false } = props

  const likeRef = useRef(likes)
  const startReceivingTimeRef = useRef<any>(Date.now())

  const [clickedAmount, setClickedAmount] = useState(0)
  const [isClick, setIsClick] = useState(false)
  const [currentLike, setCurrentLike] = useState(likes)
  const [startLikeAmount, setStartLikeAmount] = useState<any>(0)

  const setLikesMutation = useMutation(setLikes)

  const roomData = useAppSelector((state) => state.room?.roomData)
  const socketData = useAppSelector((state) => state.socket)

  const dispatch = useAppDispatch()

  const { socket } = socketData

  const { roomId = '' } = roomData

  useEffect(() => {
    // 這裡是處理使用者自己點按心時要即時顯示愛心數
    let timer: any = null

    if (Number.isInteger(likeRef.current)) {
      timer = setInterval(() => {
        const newLike = currentLike >= likeRef.current ? currentLike : likeRef.current

        setCurrentLike(newLike)
      }, throttleDuration)
    }

    return () => {
      clearInterval(timer)
    }
  }, [currentLike])

  useEffect(() => {
    likeRef.current = likes
  }, [likes])

  const handleLike = (stateData: any) => {
    dispatch(setLike({ stateData }))
  }

  const throttleLike = throttle(handleLike, throttleDuration) // 這裡是為了節流受到的 like socket message

  useEffect(() => {
    if (!socket) return

    const handleSocketRecieve = (message: any) => {
      if (message.sid === WS_EVENT_SHOP_STATE.sid) {
        if (message?.body?.type === WS_EVENT.LIKE) {
          const data = message.body
          const { content } = data

          const stateData = {
            likes: content > 0 ? content : 0,
          }

          throttleLike(stateData)
        }
      }
    }

    socket.on('CHANNEL', handleSocketRecieve)
    return () => {
      socket.off('CHANNEL', handleSocketRecieve)
    }
  }, [socket])

  // 用節流設定 like list (垂直 emoji 顯示要用)
  useEffect(() => {
    const currentTime = Date.now()
    const updateFrequency = 2000
    const currentLikeAmount = likes

    if (currentTime - startReceivingTimeRef.current >= updateFrequency) {
      if (currentLikeAmount > 0) {
        const minLikeAmount = 1
        const maxLikeAmount = 30

        if (currentLikeAmount > startLikeAmount) {
          const durationLikeAmount = currentLikeAmount - startLikeAmount
          const result = durationLikeAmount > maxLikeAmount ? random(minLikeAmount, maxLikeAmount) : durationLikeAmount

          setStartLikeAmount(currentLikeAmount)
          dispatch(setLikeList({ likes: result }))
          startReceivingTimeRef.current = Date.now()
        }
      }
    }
  }, [likes, startLikeAmount])

  const handleLikeClick = async () => {
    setClickedAmount((prev) => prev + 1)
    setCurrentLike((prev) => prev + 1)
    setIsClick(true)
    setLikesMutation.mutateAsync({ roomId, likes: 1 }) // TODO: 到時候要有 throttle 的概念，不然會狂 call API
    trackLiveEvent({ type: LIVE_TRACK_EVENT.LIKE, roomId, quantity: 1 })
    handleGtmLiveInteraction({
      interactionType: GtmLiveInteractionType.LIKE,
      hostId: roomData.memberInfoId as string,
      roomId: roomData.roomId as string,
    })
  }

  const handleAnimationEnd = () => {
    setIsClick(false)
  }

  return (
    <>
      <div
        onClick={handleLikeClick}
        onAnimationEnd={handleAnimationEnd}
        className={cx('like', { 'like--heart-beat': isClick, 'like--disable': isClick, isHost }, className)}
      >
        <div className={cx('like-count')}>
          <span className={cx('like-count-number')}>{new BigNumber(currentLike).toFormat()}</span>
        </div>
        <div className={cx('like-icon')}>
          <Icon.Like />
        </div>
      </div>

      {/* <div className={cx('emoji-animation-area')}> */}
      <Ballon />
      {!!clickedAmount && <HorizontalEmoji onChange={() => {}} amount={clickedAmount} />}
      {/* </div> */}
    </>
  )
}

export default Like
